import React, { useMemo } from 'react';

import type { DefaultTheme } from 'styled-components';
import styled, { useTheme } from 'styled-components';

import AnimatedDot from './AnimatedDot';
import { usePolyAnimationSync } from './hooks/usePolyAnimationSync';
import { getPolyLoaderConfig } from './PolyLoaderConfig';
import { delays as defaultDelays, groupings, pathData } from './pathData';

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
`;

export interface PolyLoaderProps {
  type?: 'greyscale' | 'logo';
  /** Override delay per group: [center, middle, outer] */
  delayValues?: [number, number, number];
  /** Animation duration in seconds */
  duration?: number;
}

const PolyLoaderComponent: React.FC<PolyLoaderProps> = ({
  type: type = 'greyscale',
  delayValues,
  duration,
}) => {
  const theme = useTheme() as DefaultTheme;
  const { sync: globalSync } = usePolyAnimationSync();
  const themeConfig = useMemo(() => getPolyLoaderConfig(theme), [theme]);

  const dotDelays = useMemo(
    () => (delayValues ? groupings.map(g => delayValues[g]) : defaultDelays),
    [delayValues],
  );

  return (
    <Svg
      aria-label="Poly logo"
      data-test-id="loading-icon"
      role="img"
      viewBox="0 0 32 32"
    >
      {pathData.map((d, i) => {
        const colorConfig = themeConfig[type];
        return (
          <AnimatedDot
            key={d}
            d={d}
            delay={globalSync + dotDelays[i]}
            fill={colorConfig.fill}
            fillActive={colorConfig.fillActive}
            duration={duration}
          />
        );
      })}
    </Svg>
  );
};

export const PolyLoader = React.memo(PolyLoaderComponent);
PolyLoader.displayName = 'PolyLoader';
export default PolyLoader;
